import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, ExternalLink } from "lucide-react";
import { waLink } from "@/lib/api";
import { setConsent, useCookieConsent } from "@/hooks/useCookieConsent";

const Item = ({ icon: Icon, label, value, href, testId, external }) => (
  <a
    data-testid={testId}
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    className="group flex items-center gap-4 rounded-2xl border border-line bg-white px-5 py-4 transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-clay"
  >
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-clay/10 text-clay transition-colors duration-300 group-hover:bg-clay group-hover:text-white">
      <Icon size={18} />
    </span>
    <span className="min-w-0">
      <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-fog">{label}</span>
      <span className="block break-words text-[15px] font-medium text-ink">{value}</span>
    </span>
  </a>
);

export const Contacts = ({ property }) => {
  const consent = useCookieConsent();
  const phone = property?.phone || "967 338 872";
  const email = property?.email || "Sandra.m_cardoso@hotmail.com";
  const address = property?.address || "Rua 9 de Abril, Queluz";
  const mapQuery = `${address}, Portugal`;

  return (
    <section id="contactos" data-testid="contacts-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-32">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] lg:items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.3em] text-clay">Contactos</p>
          <h2 className="font-display text-4xl leading-tight text-ink sm:text-5xl">
            Fale <em className="text-clay">connosco</em>.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-fog">
            Dúvidas sobre datas, chegada ou estadia? A Sandra responde diretamente.
          </p>
          <div className="mt-8 grid gap-3">
            <Item icon={Phone} label="Telefone" value={phone} href={`tel:+351${phone.replace(/\D/g, "")}`} testId="contact-phone-link" />
            <Item icon={Mail} label="Email" value={email} href={`mailto:${email}`} testId="contact-email-link" />
            <Item icon={MessageCircle} label="WhatsApp" value={phone} href={waLink(property?.whatsapp || "+351967338872", "Olá! Gostaria de saber mais sobre o alojamento.")} testId="contact-whatsapp-link" external />
            <Item icon={MapPin} label="Morada" value={address} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`} testId="contact-address-link" external />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden rounded-3xl border border-line bg-sand"
          data-testid="location-map"
        >
          {consent === "accepted" ? (
            <iframe
              title="Mapa — Rua 9 de Abril, Queluz"
              src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=16&output=embed&hl=pt-PT`}
              className="aspect-square w-full border-0 sm:aspect-[4/3]"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="flex aspect-square w-full flex-col items-center justify-center gap-4 p-8 text-center sm:aspect-[4/3]" data-testid="location-map-placeholder">
              <MapPin size={28} className="text-clay" />
              <p className="max-w-xs text-sm leading-relaxed text-fog">
                O mapa do Google usa cookies. Aceite para o ver aqui, ou abra a localização no Google Maps.
              </p>
              <button data-testid="location-map-accept-btn" onClick={() => setConsent("accepted")}
                className="rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-cream transition-colors duration-300 hover:bg-clay">
                Aceitar cookies e ver o mapa
              </button>
              <a data-testid="location-maps-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-clay">
                Abrir no Google Maps <ExternalLink size={13} />
              </a>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
